import { useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import { useAuth } from "@/_core/hooks/useAuth";

const CHA_CHING_URL = "/assets/sale-notification.wav";

// Preload audio on module load
let audioInstance: HTMLAudioElement | null = null;
if (typeof window !== "undefined") {
  audioInstance = new Audio(CHA_CHING_URL);
  audioInstance.volume = 0.6;
  audioInstance.preload = "auto";
}

function playChaChing() {
  if (!audioInstance) return;
  const clone = audioInstance.cloneNode() as HTMLAudioElement;
  clone.volume = 0.6;
  clone.play().catch(() => {});
}

// Platform-specific toast styles
const PLATFORM_STYLES: Record<string, { background: string; border: string; color: string; backdropFilter: string; icon: string }> = {
  hotmart: {
    background: "linear-gradient(135deg, rgba(255,111,0,0.2), rgba(255,111,0,0.05))",
    border: "1px solid rgba(255,111,0,0.4)",
    color: "#e2e8f0",
    backdropFilter: "blur(12px)",
    icon: "🔥",
  },
  greenn: {
    background: "linear-gradient(135deg, rgba(16,185,129,0.2), rgba(16,185,129,0.05))",
    border: "1px solid rgba(16,185,129,0.4)",
    color: "#e2e8f0",
    backdropFilter: "blur(12px)",
    icon: "💚",
  },
  stripe: {
    background: "linear-gradient(135deg, rgba(99,91,255,0.2), rgba(99,91,255,0.05))",
    border: "1px solid rgba(99,91,255,0.4)",
    color: "#e2e8f0",
    backdropFilter: "blur(12px)",
    icon: "💳",
  },
  default: {
    background: "linear-gradient(135deg, rgba(16,185,129,0.2), rgba(16,185,129,0.05))",
    border: "1px solid rgba(16,185,129,0.4)",
    color: "#e2e8f0",
    backdropFilter: "blur(12px)",
    icon: "💰",
  },
};

const TOAST_STYLES = {
  command: {
    background: "linear-gradient(135deg, rgba(59,130,246,0.2), rgba(59,130,246,0.05))",
    border: "1px solid rgba(59,130,246,0.4)",
    color: "#e2e8f0",
    backdropFilter: "blur(12px)",
  },
};

function getPlatformStyle(platform?: string) {
  if (!platform) return PLATFORM_STYLES.default;
  const key = platform.toLowerCase();
  return PLATFORM_STYLES[key] || PLATFORM_STYLES.default;
}

function getPlatformLabel(platform?: string): string {
  if (!platform) return "";
  const labels: Record<string, string> = {
    hotmart: "Hotmart",
    greenn: "Greenn",
    stripe: "Stripe",
  };
  return labels[platform.toLowerCase()] || platform;
}

/**
 * Hook que detecta novas vendas (via Convex reactive — sem polling) e novas
 * ordens da Central de Comando (via tRPC), mostrando toasts estilizados com
 * cores por plataforma e tocando cha-ching para vendas.
 *
 * Migração F5-12: trpc.sales.list (polling 10s) → api.sales.list (reactive).
 * Nova venda detectada quando o _id do topo muda entre renders. Convex empurra
 * a atualização via WebSocket automaticamente — não precisa refetchInterval.
 * Autopilot/command notifications continuam em tRPC (Convex pendente).
 *
 * Deve ser montado uma única vez no DashboardLayout.
 */
const NOTIFICATION_SOUND_KEY = "meta-ads-brain-notification-sound";

function isSoundEnabled(): boolean {
  return localStorage.getItem(NOTIFICATION_SOUND_KEY) !== "false";
}

export function useSaleNotification() {
  const { user } = useAuth();
  const prevLatestSaleId = useRef<string | null>(null);
  const prevCommandCount = useRef<number | null>(null);
  const hasInteracted = useRef(false);
  const soundEnabled = useRef(isSoundEnabled());
  // Track whether we've already shown a command toast for the current count
  // This prevents re-showing the same toast when useEffect re-runs due to dependency changes
  const lastCommandToastShownForCount = useRef<number | null>(null);

  // Check autopilot status — suppress command toasts when autopilot is active.
  // Migração F4: trpc.autopilot.status → api.autopilot.status (reactive Convex).
  const autopilotStatusData = useQuery(api.autopilot.status, user ? {} : "skip");
  const autopilotStatus = {
    data: autopilotStatusData,
    isLoading: autopilotStatusData === undefined,
  };
  const autopilotEnabled = autopilotStatus.data?.enabled ?? false;

  // Listen for toggle events from sidebar
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      soundEnabled.current = detail?.enabled ?? true;
    };
    window.addEventListener("notification-sound-toggle", handler);
    return () => window.removeEventListener("notification-sound-toggle", handler);
  }, []);

  // Track user interaction to enable audio
  useEffect(() => {
    const handler = () => {
      hasInteracted.current = true;
    };
    window.addEventListener("click", handler, { once: true });
    window.addEventListener("keydown", handler, { once: true });
    return () => {
      window.removeEventListener("click", handler);
      window.removeEventListener("keydown", handler);
    };
  }, []);

  // Reactive: novas vendas chegam automaticamente via WebSocket Convex —
  // sem polling, sem refetch manual. Pega só o topo (1 item) ordenado desc.
  const salesPage = useQuery(api.sales.list, user ? { limit: 1 } : "skip");
  const latestSale = salesPage?.page?.[0];
  const latestSaleId = latestSale?._id ?? null;

  // Command center counts — Migração F4: trpc.notifications.counts → api.notifications.counts.
  // Convex retorna shape rico { command: { total, new } } em vez de flat number.
  // Mantemos wrapper `.data.command` compatível: aliasamos pro `.new` (proxy de
  // "novas ordens não visitadas" — semântica usada pelo toast de INCREASE).
  const countsRaw = useQuery(api.notifications.counts, user ? {} : "skip");
  const countsQuery = {
    data: countsRaw ? { command: countsRaw.command.new } : undefined,
    isLoading: countsRaw === undefined,
  };
  const currentCommandCount = countsQuery.data?.command ?? 0;

  // Show sale toast with platform-specific colors
  const showSaleToast = useCallback((sale?: any) => {
    const productName = sale?.productName || "Novo produto";
    const amount =
      typeof sale?.amount === "number"
        ? `R$${sale.amount.toFixed(2)}`
        : sale?.amount
          ? `R$${parseFloat(sale.amount).toFixed(2)}`
          : "";
    const clientName = sale?.clientName || sale?.clientEmail || "";
    // Convex sales têm `provider` (não `source`/`platform` do shape tRPC legado).
    const platform = sale?.provider || sale?.source || sale?.platform;
    const style = getPlatformStyle(platform);
    const platformLabel = getPlatformLabel(platform);
    const platformTag = platformLabel ? ` via ${platformLabel}` : "";

    toast.success(
      `${style.icon} Venda aprovada!${platformTag}`,
      {
        description: `${productName}${amount ? ` — ${amount}` : ""}${clientName ? ` • ${clientName}` : ""}`,
        duration: 8000,
        style: {
          background: style.background,
          border: style.border,
          color: style.color,
          backdropFilter: style.backdropFilter,
        },
      }
    );

    if (hasInteracted.current && soundEnabled.current) {
      playChaChing();
    }
  }, []);

  // Show command center toast
  const showCommandToast = useCallback((count: number) => {
    if (count === 1) {
      toast.info(
        "⚡ Nova ordem de ação",
        {
          description: "O Cérebro identificou uma ação necessária. Confira na Central de Comando.",
          duration: 6000,
          style: TOAST_STYLES.command,
        }
      );
    } else {
      toast.info(
        `⚡ ${count} novas ordens de ação`,
        {
          description: "O Cérebro tem novas decisões pra você. Confira na Central de Comando.",
          duration: 6000,
          style: TOAST_STYLES.command,
        }
      );
    }
  }, []);

  // Detect nova venda: latest _id mudou (e não é primeira carga).
  useEffect(() => {
    if (latestSaleId === null) return;

    if (prevLatestSaleId.current === null) {
      // Baseline na primeira carga — não dispara toast.
      prevLatestSaleId.current = latestSaleId;
      return;
    }

    if (latestSaleId !== prevLatestSaleId.current) {
      prevLatestSaleId.current = latestSaleId;
      showSaleToast(latestSale);
    }
  }, [latestSaleId, latestSale, showSaleToast]);

  // Detect INCREASE in command center count — only when autopilot is OFF
  // AND only if we haven't already shown a toast for this exact count
  useEffect(() => {
    if (prevCommandCount.current === null) {
      prevCommandCount.current = currentCommandCount;
      lastCommandToastShownForCount.current = currentCommandCount;
      return;
    }

    if (
      currentCommandCount > (prevCommandCount.current ?? 0) &&
      currentCommandCount !== lastCommandToastShownForCount.current &&
      !autopilotEnabled
    ) {
      const newCommands = currentCommandCount - (prevCommandCount.current ?? 0);
      lastCommandToastShownForCount.current = currentCommandCount;
      showCommandToast(newCommands);
    }

    prevCommandCount.current = currentCommandCount;
  }, [currentCommandCount, autopilotEnabled, showCommandToast]);
}
